// Weekly Operations Data Entry Component
// For PRN calculation based on capacity and admissions

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Calendar, Plus, Save, Copy, TrendingUp, TrendingDown, 
  Calculator, DollarSign, Clock, Users, UserPlus, UserMinus,
  RefreshCw, ChevronRight, AlertTriangle, CheckCircle2, 
  BarChart3, Target, Sparkles, Edit, Trash2, ArrowRight
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useApiData } from '@/hooks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default form values
const defaultFormValues = {
  week_ending: new Date().toISOString().split('T')[0],
  starting_census: 0,
  ending_census: 0,
  admissions: 0,
  discharges: 0,
  deaths: 0,
  revocations: 0,
  visits_per_admission: 3.0,
  rn_hours_available: 0,
  rn_visits_completed: 0,
  rn_capacity_visits: 0,
  prn_hours_used: 0,
  prn_visits_completed: 0,
  notes: ''
};

// Calculate PRN metrics locally for preview
const calculatePRNMetrics = (data, config) => {
  const visitsPerHour = config?.visits_per_hour || 0.5;
  const prnRate = config?.prn_rate || 48;
  const shiftHours = config?.shift_hours || 8;
  
  const postAdmissionVisits = (data.admissions || 0) * (data.visits_per_admission || 3);
  const rnOverCapacity = Math.max(0, (data.rn_visits_completed || 0) - (data.rn_capacity_visits || 0));
  const totalPRNVisitsNeeded = postAdmissionVisits + rnOverCapacity;
  const prnHoursRequired = totalPRNVisitsNeeded / visitsPerHour;
  const prnShiftsRequired = Math.ceil(prnHoursRequired / shiftHours);
  const prnHoursRecommended = prnShiftsRequired * shiftHours;
  const prnCostEstimate = prnHoursRecommended * prnRate;
  
  const variance = (data.prn_hours_used || 0) - prnHoursRecommended;
  const variancePercent = prnHoursRecommended > 0 ? (variance / prnHoursRecommended) * 100 : 0;
  
  return {
    postAdmissionVisits: Math.round(postAdmissionVisits * 10) / 10,
    rnOverCapacity,
    totalPRNVisitsNeeded: Math.round(totalPRNVisitsNeeded * 10) / 10,
    prnHoursRequired: Math.round(prnHoursRequired * 10) / 10,
    prnShiftsRequired,
    prnHoursRecommended,
    prnCostEstimate: Math.round(prnCostEstimate * 100) / 100,
    variance: Math.round(variance * 10) / 10,
    variancePercent: Math.round(variancePercent * 10) / 10
  };
};

// Metric Card Component
const MetricCard = ({ icon: Icon, label, value, subtext, colorClass = 'text-cyan-400', bgClass = 'bg-cyan-500/20' }) => (
  <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${bgClass}`}>
        <Icon className={`h-5 w-5 ${colorClass}`} />
      </div>
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className={`text-xl font-bold ${colorClass}`}>{value}</p>
        {subtext && <p className="text-xs text-slate-500">{subtext}</p>}
      </div>
    </div>
  </div>
);

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-bold mb-2">{label}</p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Main Component
export default function WeeklyOperationsEntry({ token }) {
  const [activeTab, setActiveTab] = useState('entry');
  const [formData, setFormData] = useState(defaultFormValues);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  
  // Fetch entries
  const { data: entriesData, loading: entriesLoading, refetch: refetchEntries } = useApiData(
    '/api/weekly-operations/entries?weeks=12',
    { cache: false }
  );
  
  // Fetch prediction
  const { data: predictionData, loading: predictionLoading, refetch: refetchPrediction } = useApiData(
    '/api/weekly-operations/predict-next',
    { cache: false }
  );
  
  // Fetch summary
  const { data: summaryData, loading: summaryLoading } = useApiData(
    '/api/weekly-operations/summary?weeks=12',
    { cache: false }
  );
  
  const entries = entriesData?.entries || [];
  const config = entriesData?.config || { prn_rate: 48, visits_per_hour: 0.5, shift_hours: 8 };
  const prediction = predictionData?.prediction;
  const summary = summaryData?.summary;
  
  // Calculate live metrics
  const liveMetrics = calculatePRNMetrics(formData, config);
  
  // Handle form change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  // Handle number input
  const handleNumberChange = (field, value) => {
    const num = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [field]: num }));
  };
  
  // Copy from previous week
  const handleCopyPrevious = async () => {
    try {
      const response = await fetch(`${API_URL}/api/weekly-operations/copy-previous?target_week=${formData.week_ending}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      
      if (result.success && result.template) {
        setFormData(result.template);
        toast.success(`Copied data from ${result.copied_from || 'template'}`);
      }
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Failed to copy previous week');
    }
  };
  
  // Save entry
  const handleSave = async () => {
    setSaving(true);
    try {
      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing 
        ? `${API_URL}/api/weekly-operations/entry/${formData.week_ending}`
        : `${API_URL}/api/weekly-operations/entry`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(isEditing ? 'Entry updated' : 'Entry created');
        setFormData(defaultFormValues);
        setIsEditing(false);
        refetchEntries();
        refetchPrediction();
      } else {
        toast.error(result.detail || 'Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save entry');
    } finally {
      setSaving(false);
    }
  };
  
  // Edit existing entry
  const handleEdit = (entry) => {
    setFormData({
      week_ending: entry.week_ending,
      starting_census: entry.starting_census || 0,
      ending_census: entry.ending_census || 0,
      admissions: entry.admissions || 0,
      discharges: entry.discharges || 0,
      deaths: entry.deaths || 0,
      revocations: entry.revocations || 0,
      visits_per_admission: entry.visits_per_admission || 3.0,
      rn_hours_available: entry.rn_hours_available || 0,
      rn_visits_completed: entry.rn_visits_completed || 0,
      rn_capacity_visits: entry.rn_capacity_visits || 0,
      prn_hours_used: entry.prn_hours_used || 0,
      prn_visits_completed: entry.prn_visits_completed || 0,
      notes: entry.notes || ''
    });
    setIsEditing(true);
    setActiveTab('entry');
  };
  
  // Delete entry
  const handleDelete = async (weekEnding) => {
    if (!window.confirm(`Delete entry for ${weekEnding}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/weekly-operations/entry/${weekEnding}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Entry deleted');
        refetchEntries();
      } else {
        toast.error(result.detail || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete entry');
    }
  };
  
  // Prepare chart data
  const chartData = entries.slice().reverse().map(e => ({
    week: e.week_ending?.split('-').slice(1).join('/'),
    admissions: e.admissions || 0,
    prn_recommended: e.prn_calculations?.prn_hours_recommended || 0,
    prn_actual: e.prn_hours_used || 0,
    variance: e.prn_calculations?.variance_hours || 0
  }));
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Alert className="bg-gradient-to-r from-teal-900/40 to-cyan-900/40 border-teal-500/30">
        <Calendar className="h-4 w-4 text-teal-400" />
        <AlertTitle className="text-teal-400 font-bold">Weekly Operations Data Entry</AlertTitle>
        <AlertDescription className="text-teal-200 text-sm">
          Enter weekly operational data to calculate and predict PRN staffing needs. 
          Each week's data feeds into the PRN calculator for accurate forecasting.
        </AlertDescription>
      </Alert>
      
      {/* Quick Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard 
            icon={Calendar}
            label="Weeks Tracked"
            value={summary.weeks_analyzed}
            colorClass="text-cyan-400"
            bgClass="bg-cyan-500/20"
          />
          <MetricCard 
            icon={UserPlus}
            label="Avg Weekly Admissions"
            value={summary.averages.weekly_admissions}
            colorClass="text-emerald-400"
            bgClass="bg-emerald-500/20"
          />
          <MetricCard 
            icon={Clock}
            label="Avg Weekly PRN Hours"
            value={summary.averages.weekly_prn_hours}
            colorClass="text-amber-400"
            bgClass="bg-amber-500/20"
          />
          <MetricCard 
            icon={Target}
            label="Optimal Weeks"
            value={`${summary.accuracy.weeks_optimal}/${summary.weeks_analyzed}`}
            subtext={`${Math.round(summary.accuracy.weeks_optimal / summary.weeks_analyzed * 100)}% accuracy`}
            colorClass="text-violet-400"
            bgClass="bg-violet-500/20"
          />
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700/50">
          <TabsTrigger value="entry" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-400">
            <Plus className="h-4 w-4 mr-2" />
            {isEditing ? 'Edit Entry' : 'New Entry'}
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-400">
            <BarChart3 className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
          <TabsTrigger value="prediction" className="data-[state=active]:bg-teal-500/20 data-[state=active]:text-teal-400">
            <Sparkles className="h-4 w-4 mr-2" />
            Prediction
          </TabsTrigger>
        </TabsList>
        
        {/* Entry Tab */}
        <TabsContent value="entry" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Data Entry Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Week Selection */}
              <Card className="bg-slate-900/60 border-slate-700/50">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-teal-400" />
                        Week Selection
                      </CardTitle>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyPrevious}
                        className="text-slate-300 border-slate-600"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Previous
                      </Button>
                      {isEditing && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setFormData(defaultFormValues); setIsEditing(false); }}
                          className="text-slate-300 border-slate-600"
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Week Ending Date</Label>
                      <Input
                        type="date"
                        value={formData.week_ending}
                        onChange={(e) => handleChange('week_ending', e.target.value)}
                        disabled={isEditing}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Visits per Admission</Label>
                      <Input
                        type="number"
                        step="0.5"
                        min="0"
                        max="10"
                        value={formData.visits_per_admission}
                        onChange={(e) => handleNumberChange('visits_per_admission', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-500 mt-1">Average visits required per new admission</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Census & Patient Flow */}
              <Card className="bg-slate-900/60 border-slate-700/50">
                <CardHeader className="pb-4">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="h-5 w-5 text-emerald-400" />
                    Census & Patient Flow
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-300">Starting Census</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.starting_census}
                        onChange={(e) => handleNumberChange('starting_census', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Ending Census</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.ending_census}
                        onChange={(e) => handleNumberChange('ending_census', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 flex items-center gap-1">
                        <UserPlus className="h-3 w-3 text-emerald-400" />
                        Admissions
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.admissions}
                        onChange={(e) => handleNumberChange('admissions', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300 flex items-center gap-1">
                        <UserMinus className="h-3 w-3 text-rose-400" />
                        Discharges
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.discharges}
                        onChange={(e) => handleNumberChange('discharges', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Deaths</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.deaths}
                        onChange={(e) => handleNumberChange('deaths', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Revocations</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.revocations}
                        onChange={(e) => handleNumberChange('revocations', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* RN Capacity */}
              <Card className="bg-slate-900/60 border-slate-700/50">
                <CardHeader className="pb-4">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-400" />
                    RN Capacity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-slate-300">RN Hours Available</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rn_hours_available}
                        onChange={(e) => handleNumberChange('rn_hours_available', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">RN Visits Completed</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rn_visits_completed}
                        onChange={(e) => handleNumberChange('rn_visits_completed', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">RN Capacity (Max Visits)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.rn_capacity_visits}
                        onChange={(e) => handleNumberChange('rn_capacity_visits', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* PRN Actuals */}
              <Card className="bg-slate-900/60 border-slate-700/50">
                <CardHeader className="pb-4">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-400" />
                    PRN Actuals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">PRN Hours Used</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.prn_hours_used}
                        onChange={(e) => handleNumberChange('prn_hours_used', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">PRN Visits Completed</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.prn_visits_completed}
                        onChange={(e) => handleNumberChange('prn_visits_completed', e.target.value)}
                        className="bg-slate-800/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <Label className="text-slate-300">Notes (Optional)</Label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      placeholder="Any notes about this week..."
                      className="w-full mt-1 p-3 rounded-md bg-slate-800/50 border border-slate-600 text-white text-sm"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
              
              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                data-testid="save-weekly-entry-btn"
              >
                {saving ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isEditing ? 'Update Entry' : 'Save Entry'}
              </Button>
            </div>
            
            {/* Live Calculations Panel */}
            <div className="space-y-4">
              <Card className="bg-gradient-to-br from-slate-900/80 to-teal-900/20 border-teal-500/30 sticky top-4">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-teal-400" />
                    Live PRN Calculation
                  </CardTitle>
                  <CardDescription className="text-slate-400">
                    Real-time calculations based on your input
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Post-Admission Visits */}
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">Post-Admission Visits</span>
                      <span className="text-lg font-bold text-emerald-400">{liveMetrics.postAdmissionVisits}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.admissions} admissions × {formData.visits_per_admission} visits
                    </p>
                  </div>
                  
                  {/* RN Over Capacity */}
                  <div className="p-3 rounded-lg bg-slate-800/50">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-400">RN Over Capacity</span>
                      <span className={`text-lg font-bold ${liveMetrics.rnOverCapacity > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                        {liveMetrics.rnOverCapacity}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.rn_visits_completed} completed - {formData.rn_capacity_visits} capacity
                    </p>
                  </div>
                  
                  {/* Divider */}
                  <div className="border-t border-slate-700/50 pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <ArrowRight className="h-4 w-4 text-teal-400" />
                      <span className="text-sm font-medium text-teal-400">PRN Requirements</span>
                    </div>
                    
                    {/* Total PRN Visits */}
                    <div className="p-3 rounded-lg bg-teal-500/10 border border-teal-500/30">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-300">Total PRN Visits Needed</span>
                        <span className="text-xl font-bold text-teal-400">{liveMetrics.totalPRNVisitsNeeded}</span>
                      </div>
                    </div>
                    
                    {/* PRN Hours */}
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div className="p-3 rounded-lg bg-slate-800/50">
                        <p className="text-xs text-slate-400">Hours Required</p>
                        <p className="text-lg font-bold text-cyan-400">{liveMetrics.prnHoursRequired}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-800/50">
                        <p className="text-xs text-slate-400">Shifts Needed</p>
                        <p className="text-lg font-bold text-cyan-400">{liveMetrics.prnShiftsRequired}</p>
                      </div>
                    </div>
                    
                    {/* Recommended */}
                    <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-300">Recommended PRN Hours</span>
                        <span className="text-xl font-bold text-cyan-400">{liveMetrics.prnHoursRecommended}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {liveMetrics.prnShiftsRequired} shifts × {config.shift_hours || 8} hrs
                      </p>
                    </div>
                    
                    {/* Cost Estimate */}
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-slate-300 flex items-center gap-1">
                          <DollarSign className="h-4 w-4" />
                          Cost Estimate
                        </span>
                        <span className="text-xl font-bold text-amber-400">
                          ${liveMetrics.prnCostEstimate.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {liveMetrics.prnHoursRecommended} hrs × ${config.prn_rate}/hr
                      </p>
                    </div>
                  </div>
                  
                  {/* Variance (if PRN hours entered) */}
                  {formData.prn_hours_used > 0 && (
                    <div className="border-t border-slate-700/50 pt-4">
                      <div className={`p-3 rounded-lg ${
                        Math.abs(liveMetrics.variancePercent) <= 10 
                          ? 'bg-emerald-500/10 border border-emerald-500/30' 
                          : 'bg-amber-500/10 border border-amber-500/30'
                      }`}>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-300">Variance</span>
                          <div className="flex items-center gap-2">
                            {liveMetrics.variance > 0 ? (
                              <TrendingUp className="h-4 w-4 text-amber-400" />
                            ) : liveMetrics.variance < 0 ? (
                              <TrendingDown className="h-4 w-4 text-cyan-400" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                            )}
                            <span className={`text-lg font-bold ${
                              Math.abs(liveMetrics.variancePercent) <= 10 ? 'text-emerald-400' : 'text-amber-400'
                            }`}>
                              {liveMetrics.variance > 0 ? '+' : ''}{liveMetrics.variance} hrs
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {Math.abs(liveMetrics.variancePercent)}% {liveMetrics.variance > 0 ? 'over' : liveMetrics.variance < 0 ? 'under' : 'on target'}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history" className="mt-6 space-y-6">
          {/* Trends Chart */}
          {chartData.length > 0 && (
            <Card className="bg-slate-900/60 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-cyan-400" />
                  PRN Hours Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="recGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="actGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area type="monotone" dataKey="prn_recommended" name="Recommended" stroke="#06b6d4" fill="url(#recGradient)" strokeWidth={2} />
                      <Area type="monotone" dataKey="prn_actual" name="Actual" stroke="#f59e0b" fill="url(#actGradient)" strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Entries Table */}
          <Card className="bg-slate-900/60 border-slate-700/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Weekly Entries</CardTitle>
              <Button variant="outline" size="sm" onClick={refetchEntries} className="text-slate-300 border-slate-600">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent>
              {entriesLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <div key={i} className="h-16 bg-slate-800/50 rounded-lg animate-pulse" />)}
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No weekly entries yet</p>
                  <p className="text-sm text-slate-500">Start by creating your first entry</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700/50">
                        <th className="text-left py-3 px-2 text-sm text-slate-400">Week</th>
                        <th className="text-right py-3 px-2 text-sm text-slate-400">Census</th>
                        <th className="text-right py-3 px-2 text-sm text-slate-400">Admits</th>
                        <th className="text-right py-3 px-2 text-sm text-slate-400">Visits/Admit</th>
                        <th className="text-right py-3 px-2 text-sm text-slate-400">PRN Rec</th>
                        <th className="text-right py-3 px-2 text-sm text-slate-400">PRN Actual</th>
                        <th className="text-right py-3 px-2 text-sm text-slate-400">Variance</th>
                        <th className="text-right py-3 px-2 text-sm text-slate-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr key={entry.week_ending} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                          <td className="py-3 px-2 text-white font-medium">{entry.week_ending}</td>
                          <td className="py-3 px-2 text-right text-slate-300">{entry.ending_census}</td>
                          <td className="py-3 px-2 text-right text-emerald-400">{entry.admissions}</td>
                          <td className="py-3 px-2 text-right text-slate-300">{entry.visits_per_admission}</td>
                          <td className="py-3 px-2 text-right text-cyan-400">{entry.prn_calculations?.prn_hours_recommended || 0}</td>
                          <td className="py-3 px-2 text-right text-amber-400">{entry.prn_hours_used || 0}</td>
                          <td className="py-3 px-2 text-right">
                            <Badge className={
                              Math.abs(entry.prn_calculations?.variance_percent || 0) <= 10
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-amber-500/20 text-amber-400'
                            }>
                              {entry.prn_calculations?.variance_percent > 0 ? '+' : ''}
                              {entry.prn_calculations?.variance_percent || 0}%
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(entry)}>
                                <Edit className="h-4 w-4 text-slate-400 hover:text-white" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(entry.week_ending)}>
                                <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-400" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Prediction Tab */}
        <TabsContent value="prediction" className="mt-6">
          <Card className="bg-gradient-to-br from-slate-900/80 to-violet-900/20 border-violet-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-400" />
                Next Week Prediction
              </CardTitle>
              <CardDescription className="text-slate-400">
                AI-powered prediction based on historical trends
              </CardDescription>
            </CardHeader>
            <CardContent>
              {predictionLoading ? (
                <div className="h-48 bg-slate-800/50 rounded-lg animate-pulse" />
              ) : !prediction ? (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertTitle className="text-amber-400">Insufficient Data</AlertTitle>
                  <AlertDescription className="text-slate-300">
                    Need at least 2 weeks of data for prediction. Enter more weekly data to enable forecasting.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Predicting for</p>
                      <p className="text-2xl font-bold text-white">{prediction.week_ending}</p>
                    </div>
                    <Badge className={
                      prediction.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                      prediction.confidence === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-500/20 text-slate-400'
                    }>
                      {prediction.confidence} confidence
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">Expected Admissions</p>
                      <p className="text-2xl font-bold text-emerald-400">{prediction.predicted_admissions}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">Visits per Admission</p>
                      <p className="text-2xl font-bold text-white">{prediction.avg_visits_per_admission}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">PRN Visits Needed</p>
                      <p className="text-2xl font-bold text-cyan-400">{prediction.total_prn_visits_needed}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">Trend</p>
                      <div className="flex items-center gap-2">
                        {prediction.trend === 'increasing' ? (
                          <TrendingUp className="h-5 w-5 text-amber-400" />
                        ) : prediction.trend === 'decreasing' ? (
                          <TrendingDown className="h-5 w-5 text-cyan-400" />
                        ) : (
                          <ArrowRight className="h-5 w-5 text-slate-400" />
                        )}
                        <p className="text-xl font-bold text-white capitalize">{prediction.trend}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-6 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                      <div className="flex items-center gap-3">
                        <Clock className="h-8 w-8 text-cyan-400" />
                        <div>
                          <p className="text-sm text-slate-400">Recommended PRN Hours</p>
                          <p className="text-3xl font-bold text-cyan-400">{prediction.prn_hours_recommended}</p>
                          <p className="text-sm text-slate-500">{prediction.prn_shifts_recommended} shifts</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6 rounded-xl bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-center gap-3">
                        <DollarSign className="h-8 w-8 text-amber-400" />
                        <div>
                          <p className="text-sm text-slate-400">Estimated Cost</p>
                          <p className="text-3xl font-bold text-amber-400">
                            ${prediction.prn_cost_estimate.toLocaleString()}
                          </p>
                          <p className="text-sm text-slate-500">@ ${config.prn_rate}/hr</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button onClick={refetchPrediction} variant="outline" className="text-slate-300 border-slate-600">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Prediction
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
